import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phase, PhaseDisplayNames, PhaseColors } from '../../types/bom';

interface ChecklistItem {
  item: string;
  required: boolean;
  checked?: boolean;
  checked_by?: string;
  checked_at?: string;
  notes?: string;
}

interface ReadinessBlocker {
  type: string;
  count?: number;
  details?: unknown;
}

interface ReadinessWarning {
  type: string;
  count?: number;
  details?: unknown;
}

interface PhaseReadiness {
  product_id: string;
  current_phase: Phase;
  next_phase: Phase;
  ready: boolean;
  blockers?: ReadinessBlocker[];
  warnings?: ReadinessWarning[];
  checklist_template: ChecklistItem[];
}

const PhaseTransition: React.FC = () => {
  const { t } = useTranslation();
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [readiness, setReadiness] = useState<PhaseReadiness | null>(null);
  const [checklist, setChecklist] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (productId) {
      loadReadiness();
    }
  }, [productId]);

  const loadReadiness = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/v1/bom/products/${productId}/readiness`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load readiness data');
      }

      const data = await response.json();
      setReadiness(data);

      // Initialize checklist state
      const initialChecklist: Record<string, boolean> = {};
      data.checklist_template?.forEach((item: ChecklistItem) => {
        initialChecklist[item.item] = false;
      });
      setChecklist(initialChecklist);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleChecklistChange = (item: string, checked: boolean) => {
    setChecklist(prev => ({ ...prev, [item]: checked }));
  };

  const handleTransition = async () => {
    if (!readiness) return;

    // Validate all required items are checked
    const requiredItems = readiness.checklist_template?.filter(item => item.required) || [];
    const allRequiredChecked = requiredItems.every(item => checklist[item.item]);

    if (!allRequiredChecked) {
      setError('Please complete all required checklist items');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/v1/bom/products/${productId}/transition`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          new_phase: readiness.next_phase,
          checklist,
          notes,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to transition phase');
      }

      // Success - navigate back to product detail
      navigate(`/app/bom/products/${productId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setSubmitting(false);
    }
  };

  const getBlockerIcon = (type: string) => {
    switch (type) {
      case 'tbd_parts': return '🔴';
      case 'no_builds': return '🏗️';
      case 'no_completed_builds': return '⏳';
      case 'no_revision': return '📄';
      case 'terminal_phase': return '🏁';
      default: return '⚠️';
    }
  };

  const getBlockerMessage = (blocker: ReadinessBlocker) => {
    switch (blocker.type) {
      case 'tbd_parts':
        return `${blocker.count} TBD parts need to be resolved`;
      case 'no_builds':
        return blocker.details as string;
      case 'no_completed_builds':
        return blocker.details as string;
      case 'no_revision':
        return 'No BOM revision exists for current phase';
      case 'terminal_phase':
        return 'Product is already at Mass Production phase';
      default:
        return String(blocker.details || blocker.type);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!readiness) {
    return (
      <div className="p-6">
        <div className="text-center text-red-600 py-8">{error || 'Failed to load readiness data'}</div>
        <button
          onClick={() => navigate(`/app/bom/products/${productId}`)}
          className="mx-auto block px-4 py-2 text-blue-600 hover:underline"
        >
          {t('common.back', 'Back to Product')}
        </button>
      </div>
    );
  }

  const phases: Phase[] = ['pre_bom', 'evt', 'dvt', 'pvt', 'mp'];
  const currentPhaseIndex = phases.indexOf(readiness.current_phase);
  const nextPhaseIndex = phases.indexOf(readiness.next_phase);
  const hasBlockers = readiness.blockers && readiness.blockers.length > 0;
  const allRequiredChecked = readiness.checklist_template?.filter(item => item.required).every(item => checklist[item.item]) ?? true;
  const canTransition = !hasBlockers && allRequiredChecked;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/app/bom/products/${productId}`)}
          className="text-sm text-blue-600 hover:underline mb-2"
        >
          &larr; {t('common.back', 'Back to Product')}
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {t('bom.phaseTransition', 'Phase Transition')}
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          {PhaseDisplayNames[readiness.current_phase]} &rarr; {PhaseDisplayNames[readiness.next_phase]}
        </p>
      </div>

      {/* Phase Progress */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="flex items-center justify-between">
          {phases.map((phase, index) => (
            <React.Fragment key={phase}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-white text-sm font-medium transition-all ${
                    index < currentPhaseIndex ? PhaseColors[phase] :
                    index === currentPhaseIndex ? `${PhaseColors[phase]} ring-4 ring-offset-2 ring-blue-300` :
                    index === nextPhaseIndex ? 'bg-gray-300 dark:bg-gray-600 ring-2 ring-dashed ring-blue-400' :
                    'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  {index + 1}
                </div>
                <span
                  className={`mt-2 text-sm ${
                    index === currentPhaseIndex ? 'font-bold text-blue-600' :
                    index === nextPhaseIndex ? 'font-medium text-green-600' :
                    'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {PhaseDisplayNames[phase]}
                </span>
              </div>
              {index < phases.length - 1 && (
                <div
                  className={`flex-1 h-1 mx-2 ${
                    index < currentPhaseIndex ? 'bg-green-500' :
                    index === currentPhaseIndex ? 'bg-gradient-to-r from-green-500 to-gray-300' :
                    'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 dark:bg-red-900/20 dark:border-red-800">
          {error}
        </div>
      )}

      {/* Blockers */}
      {hasBlockers && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-red-800 dark:text-red-200 mb-3">
            {t('bom.blockers', 'Blockers')} ({readiness.blockers?.length})
          </h3>
          <ul className="space-y-2">
            {readiness.blockers?.map((blocker, index) => (
              <li key={index} className="flex items-start gap-2 text-red-700 dark:text-red-300">
                <span>{getBlockerIcon(blocker.type)}</span>
                <span>{getBlockerMessage(blocker)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Warnings */}
      {readiness.warnings && readiness.warnings.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-yellow-800 dark:text-yellow-200 mb-3">
            {t('bom.warnings', 'Warnings')} ({readiness.warnings.length})
          </h3>
          <ul className="space-y-2">
            {readiness.warnings.map((warning, index) => (
              <li key={index} className="flex items-start gap-2 text-yellow-700 dark:text-yellow-300">
                <span>⚠️</span>
                <span>{String(warning.details || warning.type)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Checklist */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">
          {t('bom.phaseChecklist', 'Phase Gate Checklist')}
        </h3>
        <div className="space-y-3">
          {readiness.checklist_template?.map((item, index) => (
            <label
              key={index}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                checklist[item.item]
                  ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
                  : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
              }`}
            >
              <input
                type="checkbox"
                checked={checklist[item.item] || false}
                onChange={(e) => handleChecklistChange(item.item, e.target.checked)}
                disabled={hasBlockers}
                className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className={`text-gray-900 dark:text-gray-100 ${checklist[item.item] ? 'line-through opacity-70' : ''}`}>
                    {item.item}
                  </span>
                  {item.required && (
                    <span className="text-xs text-red-500 font-medium">*Required</span>
                  )}
                </div>
              </div>
              {checklist[item.item] && (
                <span className="text-green-600">✓</span>
              )}
            </label>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">
          {t('bom.transitionNotes', 'Transition Notes')}
        </h3>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes about this phase transition..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />
      </div>

      {/* Actions */}
      <div className="flex gap-4">
        <button
          onClick={handleTransition}
          disabled={!canTransition || submitting}
          className={`flex-1 px-6 py-3 rounded-lg font-medium transition-colors ${
            canTransition
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
          }`}
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
              Transitioning...
            </span>
          ) : (
            <>
              {t('bom.confirmTransition', 'Confirm Transition to')} {PhaseDisplayNames[readiness.next_phase]}
            </>
          )}
        </button>
        <button
          onClick={() => navigate(`/app/bom/products/${productId}`)}
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          {t('common.cancel', 'Cancel')}
        </button>
      </div>
    </div>
  );
};

export default PhaseTransition;
